/**
 * SpatialHash — generic spatial hash grid for fast proximity queries.
 *
 * Usage:
 *   const hash = new SpatialHash<Enemy>(40);
 *   hash.rebuild(enemies, e => e.body.x, e => e.body.y);
 *   const nearby = hash.queryRadius(px, py, 60);
 *
 * Rebuild once per frame (clear + reinsert). No incremental updates needed.
 */
export class SpatialHash<T> {
  private cellSize: number;
  private invCell: number;
  private cells: Map<number, T[]> = new Map();

  constructor(cellSize: number) {
    this.cellSize = cellSize;
    this.invCell = 1 / cellSize;
  }

  // ── Public API ──

  /** Clear all buckets. */
  clear(): void {
    this.cells.clear();
  }

  /** Insert a single item at (x, y). */
  insert(item: T, x: number, y: number): void {
    const key = this.hash(x, y);
    let bucket = this.cells.get(key);
    if (!bucket) {
      bucket = [];
      this.cells.set(key, bucket);
    }
    bucket.push(item);
  }

  /** Clear and rebuild from an array using accessor callbacks. */
  rebuild(items: T[], getX: (t: T) => number, getY: (t: T) => number): void {
    this.cells.clear();
    for (let i = 0; i < items.length; i++) {
      this.insert(items[i], getX(items[i]), getY(items[i]));
    }
  }

  /**
   * Return all items in cells that overlap the circle (cx, cy, radius).
   * NOTE: results may include items outside the radius — caller should
   * do a final distance check if precision is needed.
   */
  queryRadius(cx: number, cy: number, radius: number): T[] {
    const minCX = Math.floor((cx - radius) * this.invCell);
    const maxCX = Math.floor((cx + radius) * this.invCell);
    const minCY = Math.floor((cy - radius) * this.invCell);
    const maxCY = Math.floor((cy + radius) * this.invCell);

    const result: T[] = [];
    for (let gx = minCX; gx <= maxCX; gx++) {
      for (let gy = minCY; gy <= maxCY; gy++) {
        const bucket = this.cells.get(this.hashCell(gx, gy));
        if (bucket) {
          for (let i = 0; i < bucket.length; i++) {
            result.push(bucket[i]);
          }
        }
      }
    }
    return result;
  }

  /**
   * Return all items in cells overlapping the rectangle (x, y, w, h).
   */
  queryRect(x: number, y: number, w: number, h: number): T[] {
    const minCX = Math.floor(x * this.invCell);
    const maxCX = Math.floor((x + w) * this.invCell);
    const minCY = Math.floor(y * this.invCell);
    const maxCY = Math.floor((y + h) * this.invCell);

    const result: T[] = [];
    for (let gx = minCX; gx <= maxCX; gx++) {
      for (let gy = minCY; gy <= maxCY; gy++) {
        const bucket = this.cells.get(this.hashCell(gx, gy));
        if (bucket) {
          for (let i = 0; i < bucket.length; i++) {
            result.push(bucket[i]);
          }
        }
      }
    }
    return result;
  }

  // ── Internals ──

  /** Hash world-space coords to a cell key. */
  private hash(x: number, y: number): number {
    const cx = Math.floor(x * this.invCell);
    const cy = Math.floor(y * this.invCell);
    return this.hashCell(cx, cy);
  }

  /** Hash grid-space cell coords to a unique integer key. */
  private hashCell(cx: number, cy: number): number {
    // Spatial hash primes (Teschner et al.)
    return (cx * 73856093) ^ (cy * 19349663);
  }
}
